import { FishingReport, ProductFamily, TechniqueFull, TechniqueKeyword } from '@omniafishing/core';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { UserProfileImg } from '../../components/user_profile_img/user_profile_img';
import { UserProfileLink } from '../../components/user_profile_link/user_profile_link';
import { useEventListener } from '../../hooks/use_event_listener';
import { NAME_FALLBACK } from '../../lib/user';
import { WebAnalytics } from '../../lib/web_analytics';
import styles from './fishing_report.less';

export const StyleKeywordFishingReport = (props: {
  fishing_report: FishingReport;
  style: TechniqueFull;
  techniqueKeyword: TechniqueKeyword;
  productFamily: ProductFamily;
}) => {
  const { fishing_report, style, techniqueKeyword, productFamily } = props;
  const { user, waterbody, comments } = fishing_report;
  const commentsRef = useRef<HTMLParagraphElement>();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const checkOverflow = () => {
    if (commentsRef.current) {
      setHasOverflow(commentsRef.current.scrollHeight > commentsRef.current.clientHeight);
    }
  };

  useEventListener('resize', _.debounce(checkOverflow, 500));
  useEffect(() => {
    checkOverflow();
  }, []);

  const commentsHtml = comments
    .toLowerCase()
    .includes(fishing_report.style.display_name.toLowerCase())
    ? comments.replace(new RegExp(fishing_report.style.display_name, 'ig'), '<em>$&</em>')
    : `${user.full_name || NAME_FALLBACK} reported catching ${
        fishing_report.species.display_name
      } using the ${style.display_name} technique and ${productFamily.title} on ${
        fishing_report.waterbody.primary_name
      }. Additional comments from this report: "${comments}"`;

  const webAnalyticsArgs = {
    product_category: techniqueKeyword.category,
    product_subcategory: techniqueKeyword.subcategory,
    technique: style.name,
    keyword_slug: techniqueKeyword.slug,
  };

  return (
    <div className={styles.fishingReport}>
      <div className={styles.details}>
        <UserProfileLink
          role={user.role}
          slug={user.slug}
          onClick={() => {
            WebAnalytics.dynamicSeoPageClick('[fishing_report].(user_link)', webAnalyticsArgs);
          }}
        >
          <UserProfileImg image={user.image} imageSize={60} />
        </UserProfileLink>
        <div className={styles.text}>
          <p>
            Reported by
            <br />
            <UserProfileLink
              role={user.role}
              slug={user.slug}
              className={styles.name}
              onClick={() => {
                WebAnalytics.dynamicSeoPageClick('[fishing_report].(user_link)', webAnalyticsArgs);
              }}
            >
              {user.full_name || NAME_FALLBACK}
            </UserProfileLink>
          </p>
          <p>
            {waterbody.primary_name}{' '}
            {waterbody.locales.length > 0 && (
              <>({waterbody.locales.map((l) => l.state.abbr).join(', ')})</>
            )}
          </p>
        </div>
      </div>
      <div className={classNames(styles.commentsWrapper)}>
        <p
          className={classNames(styles.comments, {
            [styles.comments__clamped]: !showOverflow,
          })}
          ref={commentsRef}
          dangerouslySetInnerHTML={{
            __html: commentsHtml,
          }}
        />
        {hasOverflow && !showOverflow && (
          <span
            onClick={() => {
              setShowOverflow(true);
              WebAnalytics.dynamicSeoPageClick('[fishing_report].(show_more)', webAnalyticsArgs);
            }}
            className={styles.more}
          >
            More
          </span>
        )}
      </div>
    </div>
  );
};
